import { UserAction, type Video } from "@brookbush/types";

import { useAuth } from "@clerk/nextjs";
import { setCookie } from "cookies-next";
import { memo, useState } from "react";

import PaywallPriceLine from "~/components/PaywallPriceLine";
import VideoPlayer from "~/components/VideoPlayer";
import { useAccess, useAuditedVideos, useUserAction } from "~/hooks";
import useConversionFlag from "~/hooks/useConversionFlag";
import useTrackCTA from "~/hooks/useTrackCTA";
import {
	CONVERSION_FLAGS,
	isConversionFlagOn,
} from "~/lib/conversion-test-flags";

const VideoBody = ({ video }: { video: Video }) => {
	const [timestamp, setTimestamp] = useState(0);
	const { isMember, isLoadingAccess } = useAccess();
	const { isSignedIn, userId } = useAuth();

	const { auditedVideos } = useAuditedVideos();
	const trackCTA = useTrackCTA();

	const { setUserAction } = useUserAction();

	const [isPreviewEnded, setIsPreviewEnded] = useState(false);

	const isAudited = auditedVideos?.find(
		(auditedVideo) => auditedVideo.videoId === video._id,
	);

	const hasVideoAccess = Boolean(isMember || isAudited);

	const { value: paywallPriceFlag } = useConversionFlag(
		CONVERSION_FLAGS.paywallPrice,
		{ enabled: isPreviewEnded },
	);
	const isPaywallPriceOn = isConversionFlagOn(paywallPriceFlag);

	const openPaywallAction = () => {
		if (!isSignedIn) {
			trackCTA(UserAction.SIGN_UP, "Video Player");

			setCookie("audited-video-id", video._id);

			setUserAction(UserAction.SIGN_UP);
		} else {
			trackCTA(UserAction.BECOME_A_MEMBER, "Video Player");
			setUserAction(UserAction.BECOME_A_MEMBER);
		}
	};

	return (
		<main id="video" className="h-full w-full">
			<VideoPlayer
				duration={video.duration}
				playbackId={video.playbackId}
				thumbnailUrl={video.thumbnailUrl}
				blurDataURL={video.blurDataURL}
				onTimeUpdate={(e: any) => {
					// Preview countdown only needs whole seconds; skip work for
					// members and when the floored second has not changed.
					if (hasVideoAccess) return;

					const currentTime = e.target.currentTime as number;
					if (currentTime >= 30) {
						setIsPreviewEnded(true);
						e.target.pause();
						return;
					}

					const seconds = Math.floor(currentTime);
					setTimestamp((prev) => (prev === seconds ? prev : seconds));
				}}
				onLoadedMetadata={() => {
					if (video.chapters) {
						const player = document.querySelector("mux-player");
						const playerEl = player as any;
						playerEl.addChapters(
							video.chapters.map((chapter) => ({
								startTime: chapter.startTime,
								value: chapter.value,
							})),
						);
					}
				}}
				style={
					isPreviewEnded
						? ({
								"--controls": "none",
								aspectRatio: video?.aspectRatio,
							} as any)
						: {
								aspectRatio: video?.aspectRatio,
							}
				}
				metadata={{
					title: video.title,
					videoId: video._id,
					viewerUserId: isSignedIn ? userId : undefined,
				}}
			/>

			{!isPreviewEnded && !hasVideoAccess && !isLoadingAccess && (
				<div className="absolute top-4 right-4 flex rounded-full border bg-white/90 px-3 py-1 font-medium text-black shadow-black/5 shadow-xl backdrop-blur sm:px-4 sm:py-2 sm:text-lg dark:border-slate-200 dark:text-black">
					{timestamp === 0
						? "Free Preview"
						: `${Math.max(0, 30 - timestamp)}s left`}
				</div>
			)}

			{isPreviewEnded && (
				<div className="absolute top-0 right-0 bottom-0 left-0 flex h-full w-full flex-col items-center justify-center gap-6 bg-black/60 p-6 text-center sm:gap-8 sm:p-28">
					{isPaywallPriceOn ? (
						<button
							type="button"
							onClick={openPaywallAction}
							className="font-semibold text-white text-xl hover:underline sm:text-3xl lg:text-4xl"
						>
							Become a member to get full access to this video.
						</button>
					) : (
						<h2 className="font-semibold text-white text-xl sm:text-3xl lg:text-4xl">
							Become a member to get full access to this video.
						</h2>
					)}

					<div className="flex flex-col items-center gap-3">
						<button
							onClick={openPaywallAction}
							className="rounded-full bg-white px-4 py-1.5 font-medium text-slate-700 hover:brightness-90 sm:px-6 sm:py-2.5 sm:text-xl"
						>
							{!isSignedIn ? "Create an account" : "Become a member"}
						</button>

						{isSignedIn && (
							<PaywallPriceLine className="text-sm text-white/90 sm:text-base" />
						)}
					</div>
				</div>
			)}
		</main>
	);
};

export default memo(VideoBody);
